/* =========================================================
   Jaiman Toys — Gift with Purchase engine
   Rules live in Admin → Content → Metaobjects → Gift with Purchase.
   After every cart change it reads /cart?view=jtgwp (Liquid works out
   entitlements) and adds / removes / caps gift lines to match.
   ============================================================ */
(() => {
  if (window.__jtGwpLoaded) return;
  window.__jtGwpLoaded = true;

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const DISMISS_KEY = 'jtGwpDismissed';
  const TIMER_KEY = 'jtCartTimerStart';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} },
  };
  const getDismissed = () => { try { return JSON.parse(store.get(DISMISS_KEY) || '{}'); } catch (e) { return {}; } };
  const setDismissed = (o) => store.set(DISMISS_KEY, JSON.stringify(o));

  const nativeFetch = window.fetch.bind(window);
  let syncing = false, queued = false, debounce;
  const LAST_KEY = 'jtGwpLastQty';
  let lastQty = (() => { try { return JSON.parse(store.get(LAST_KEY) || 'null'); } catch (e) { return null; } })();
  let giftVariants = [];       // for decorating cart rows

  const schedule = () => { clearTimeout(debounce); debounce = setTimeout(sync, 350); };

  /* ---- Watch every cart change made by the theme or apps ---- */
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const p = nativeFetch(input, init);
    if (CART_MUTATION.test(url)) p.then(schedule).catch(() => {});
    return p;
  };
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (m, url) { this._jtUrl = String(url || ''); return origOpen.apply(this, arguments); };
  XMLHttpRequest.prototype.send = function () {
    if (CART_MUTATION.test(this._jtUrl || '')) this.addEventListener('load', schedule);
    return origSend.apply(this, arguments);
  };

  async function post(path, body) {
    const r = await nativeFetch(ROOT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error('JT GWP: ' + path + ' ' + r.status);
    return r.json();
  }

  async function readState() {
    // NOTE: no "Accept: application/json" header here — with it, Shopify ignores
    // ?view=jtgwp and returns the standard cart JSON instead of our template.
    const r = await nativeFetch(ROOT + 'cart?view=jtgwp&_=' + Date.now(), { headers: { Accept: 'text/html' }, cache: 'no-store' });
    const data = JSON.parse(await r.text());
    if (!data || !Array.isArray(data.giftVariants)) throw new Error('JT GWP: cart.jtgwp template not found on this theme');
    return data;
  }

  async function sync() {
    if (syncing) { queued = true; return; }
    syncing = true;
    try {
      const state = await readState();
      giftVariants = (state.giftVariants || []).map(String);

      // Entitlement per gift variant (rules sharing a gift are summed, max 10)
      const want = {}, available = {};
      (state.rules || []).forEach((r) => {
        const v = String(r.variant);
        want[v] = Math.min(10, (want[v] || 0) + (Number(r.want) || 0));
        available[v] = available[v] || !!r.available;
      });

      const inCart = {};
      Object.keys(state.inCart || {}).forEach((k) => { inCart[String(k)] = Number(state.inCart[k]) || 0; });

      const dismissed = getDismissed();
      const adds = [], updates = {};

      giftVariants.forEach((v) => {
        const target = want[v] || 0;
        const current = inCart[v] || 0;
        const before = lastQty ? lastQty[v] || 0 : null;

        if (target === 0) {
          delete dismissed[v];                       // reset choice once no longer eligible
          if (current > 0) updates[v] = 0;
          return;
        }
        // Customer deleted a gift they were entitled to → respect it
        if (current === 0 && before > 0) dismissed[v] = true;

        if (current > target) updates[v] = target;
        else if (current < target && !dismissed[v] && available[v]) {
          if (current === 0) adds.push({ id: Number(v), quantity: target });
          else updates[v] = target;
        }
      });
      setDismissed(dismissed);

      let changed = false;
      try {
        if (Object.keys(updates).length) { await post('cart/update.js', { updates }); changed = true; }
        if (adds.length) { await post('cart/add.js', { items: adds }); changed = true; }
      } catch (err) { console.warn(err); }   // never block the customer's cart

      // Remember resulting gift quantities
      const after = {};
      giftVariants.forEach((v) => {
        let q = inCart[v] || 0;
        if (v in updates) q = updates[v];
        const a = adds.find((x) => String(x.id) === v);
        if (a) q = a.quantity;
        after[v] = q;
      });
      lastQty = after;
      store.set(LAST_KEY, JSON.stringify(after));

      if (changed) await refreshUI();
      decorate();
    } catch (err) {
      console.warn('JT GWP sync error', err);
    } finally {
      syncing = false;
      if (queued) { queued = false; schedule(); }
    }
  }

  /* ---- Re-render Dawn's cart drawer + cart icon bubble ---- */
  async function refreshUI() {
    if (/\/cart\/?$/.test(location.pathname)) { location.reload(); return; }
    try {
      const data = await (await nativeFetch(ROOT + '?sections=cart-drawer,cart-icon-bubble')).json();
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
      if (data['cart-drawer']) {
        const doc = parse(data['cart-drawer']);
        const fresh = doc.querySelector('.drawer__inner');
        const live = document.querySelector('#CartDrawer .drawer__inner');
        if (fresh && live) live.innerHTML = fresh.innerHTML;
        const fd = doc.querySelector('cart-drawer'), ld = document.querySelector('cart-drawer');
        if (fd && ld) ld.classList.toggle('is-empty', fd.classList.contains('is-empty'));
      }
      if (data['cart-icon-bubble']) {
        const src = parse(data['cart-icon-bubble']).querySelector('.shopify-section');
        const bubble = document.getElementById('cart-icon-bubble');
        if (src && bubble) bubble.innerHTML = src.innerHTML;
      }
      document.dispatchEvent(new CustomEvent('jt:gwp:updated'));
    } catch (err) { console.warn('JT GWP refresh error', err); }
  }

  /* ---- Tag gift rows (FREE GIFT label, no qty stepper) ---- */
  function decorate() {
    document.querySelectorAll('.cart-item').forEach((row) => {
      const link = row.querySelector('a[href*="variant="]');
      const m = link && link.getAttribute('href').match(/variant=(\d+)/);
      row.classList.toggle('jt-gift-line', !!(m && giftVariants.includes(m[1])));
    });
  }

  /* ---- Countdown pill (from the cart-rewards header) ---- */
  function tickTimers() {
    const els = document.querySelectorAll('[data-jt-timer]');
    if (!els.length) return;
    const total = (Number(els[0].dataset.minutes) || 10) * 60000;
    let start = Number(store.get(TIMER_KEY));
    const now = Date.now();
    if (!start || now - start >= total) { start = now; store.set(TIMER_KEY, String(start)); }
    const left = Math.max(0, total - (now - start));
    const txt = String(Math.floor(left / 60000)).padStart(2, '0') + ':' + String(Math.floor((left % 60000) / 1000)).padStart(2, '0') + ' mins';
    els.forEach((el) => { el.textContent = txt; });
  }

  function init() {
    tickTimers();
    setInterval(tickTimers, 1000);
    const drawer = document.getElementById('CartDrawer');
    if (drawer) new MutationObserver(decorate).observe(drawer, { childList: true, subtree: true });
    if (Number((window.JTGift || {}).cartCount) > 0) sync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
